import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Bar, BarChart, LabelList, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { useAtom } from "jotai";
import { Document, Page, View, Image, PDFViewer, Text } from "@react-pdf/renderer";
import {
  displayMunicNameAtom,
  establishmentsVsEmploymentDataAtom,
  firmSizeRangeAtom,
  imageResponseAtom,
  selectedFiltersAtom,
  selectedPlaceAtom,
  selectedSpatialLayerAtom,
  selectedSpatialValueAtom,
  spatialParameterAtom,
} from "../../shared/state/atoms";
import axiosNoAuthInstance from "../../components/axiosApiNoAuth";
import { Box, CircularProgress } from "@mui/material";
import styles from "../styles/PdfStyles";

// Same colour swatch as the interactive dashboard
const SEGMENT_COLORS = ['#009640', '#66bb6a', '#84746C', '#F5C242', '#F0A400'];

const SegmentLabel = (props) => {
  const { x, y, width, height, value } = props;
  if (!value || height < 14) return null;
  return (
    <text x={x + width / 2} y={y + height / 2} fill="#fff" textAnchor="middle" dominantBaseline="middle" fontSize={height < 20 ? 9 : 11} fontWeight="bold">
      {value.toFixed(1)}%
    </text>
  );
};

const roundAndNormalize = (items) => {
  const result = {};
  const rounded = items.map((item) => ({
    segment: item.segment,
    value: Math.round(item.percentage * 10) / 10,
  }));
  const sum = rounded.reduce((s, r) => s + r.value, 0);
  const diff = Math.round((100 - sum) * 10) / 10;
  if (diff !== 0 && rounded.length > 0) {
    rounded[rounded.length - 1].value = Math.round((rounded[rounded.length - 1].value + diff) * 10) / 10;
  }
  rounded.forEach((r) => { result[r.segment] = r.value; });
  return result;
};

const transformResponse = (responseData) => {
  if (!responseData) return null;
  const estData = roundAndNormalize(responseData.establishments);
  const empData = roundAndNormalize(responseData.employment);
  return {
    chartData: [
      { name: 'Establishments', ...estData },
      { name: 'Employment', ...empData },
    ],
    segments: responseData.establishments.map((item) => ({
      segment: item.segment,
      label: item.label,
    })),
  };
};

function EstablishmentsVsEmploymentPdf() {
  const [displayMunicname] = useAtom(displayMunicNameAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [selectedSpatialValue] = useAtom(selectedSpatialValueAtom);
  const [spatialParameter] = useAtom(spatialParameterAtom);
  const [imageResponse] = useAtom(imageResponseAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);
  const [existingData] = useAtom(establishmentsVsEmploymentDataAtom);

  // Place data comes from the atom (already loaded by the interactive dashboard)
  const placeData = existingData?.chartData ? existingData : null;
  const [provinceData, setProvinceData] = useState(null);
  const [nationalData, setNationalData] = useState(null);
  const [chartImageUrl, setChartImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const chartRef = useRef();

  const handles = firmSizeRange?.establishmentsVsEmployment?.handles ?? [10, 50, 250, 1000];
  const handleParams = `&handle1=${handles[0]}&handle2=${handles[1]}&handle3=${handles[2]}&handle4=${handles[3]}`;

  const isProvinceSpatial = selectedSpatialLayer?.id === "province";
  const provinceCode = isProvinceSpatial ? selectedSpatialValue : selectedPlace?.province;
  const provinceDisplayName = isProvinceSpatial ? selectedPlace?.name : (selectedPlace?.province_n || provinceCode);
  const placeDisplayName = selectedPlace?.name || selectedSpatialValue;

  // Fetch province + national data (place data comes from atom)
  useEffect(() => {
    if (!placeData) return; // Wait for atom to hydrate

    async function fetchData() {
      const baseUrl = "/api/dashboard-establishments-vs-employment/";
      const yearParam = `&year=${selectedFilters?.singleYear}`;
      const calls = [];

      if (!isProvinceSpatial && provinceCode) {
        calls.push(
          axiosNoAuthInstance.get(`${baseUrl}?province=${provinceCode}${yearParam}${handleParams}`)
            .then((r) => ({ key: "province", data: r.data }))
            .catch(() => ({ key: "province", data: null }))
        );
      }

      calls.push(
        axiosNoAuthInstance.get(`${baseUrl}?national=1${yearParam}${handleParams}`)
          .then((r) => ({ key: "national", data: r.data }))
          .catch(() => ({ key: "national", data: null }))
      );

      const results = await Promise.all(calls);
      for (const { key, data } of results) {
        if (key === "province" && data) setProvinceData(transformResponse(data));
        if (key === "national" && data) setNationalData(transformResponse(data));
      }
      setDataReady(true);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeData]);

  // Capture chart image once data is ready
  useEffect(() => {
    if (!dataReady) return;
    setTimeout(() => {
      if (!chartRef.current) return;
      toPng(chartRef.current, { cacheBust: true, pixelRatio: 2 })
        .then((dataUrl) => {
          setChartImageUrl(dataUrl);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Failed to capture Establishments & Jobs chart:", err);
          setIsLoading(false);
        });
    }, 2000);
  }, [dataReady]);

  const segments = placeData?.segments || nationalData?.segments || [];
  const segmentNames = segments.map((s) => s.segment);

  // Build chart list matching the interactive dashboard layout
  const charts = [];
  if (!isProvinceSpatial && placeData) {
    charts.push({ data: placeData, subtitle: placeDisplayName });
  }
  if (isProvinceSpatial && placeData) {
    charts.push({ data: placeData, subtitle: provinceDisplayName });
  }
  if (!isProvinceSpatial && provinceData) {
    charts.push({ data: provinceData, subtitle: provinceDisplayName });
  }
  if (nationalData) {
    charts.push({ data: nationalData, subtitle: "South Africa" });
  }

  const chartWidthPx = charts.length > 0 ? Math.floor(700 / charts.length) : 700;

  const LegendSection = () => (
    <View style={{ ...styles.legendSection, marginBottom: 10, marginTop: -10, flexDirection: "row", justifyContent: "center" }}>
      {segments.map((seg, i) => (
        <View key={seg.segment} style={{ ...styles.legendLine, marginRight: 12 }}>
          <View style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <View style={{ ...styles.legend, backgroundColor: SEGMENT_COLORS[i], width: 12, height: 12, marginRight: 4 }} />
            <Text style={{ ...styles.legendText, fontSize: 9 }}>{seg.segment}: {seg.label}</Text>
          </View>
        </View>
      ))}
    </View>
  );

  return (
    <>
      {isLoading ? (
        <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
          <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
            <CircularProgress color="success" size="3rem" />
          </Box>
        </div>
      ) : chartImageUrl ? (
        <PDFViewer style={{ flex: 1, width: "100%", height: "100vh" }}>
          <Document>
            <Page size="A4" style={styles.page} break>
              <Image style={styles.headerLogo} src={`data:image/png;base64,'${imageResponse[13].base64_image}'`}></Image>
              <Text style={styles.headerText}>{selectedSpatialLayer?.name} | {displayMunicname}</Text>
              <View>
                <Text style={{ ...styles.header, backgroundColor: "#1976d2" }}>Small Business</Text>
              </View>
              <View style={styles.section}>
                <Text style={styles.heading}>ESTABLISHMENTS & JOBS</Text>
              </View>
              <View style={{ ...styles.section, paddingBottom: 0 }}>
                <Text style={styles.subheading}>Year: {selectedFilters?.singleYear}</Text>
              </View>
              <View style={styles.section}>
                <Image style={{ ...styles.image, marginTop: 10 }} src={chartImageUrl} />
              </View>
              <LegendSection />
              <View style={{ position: "absolute", bottom: 60, left: 30, right: 30 }}>
                <Text style={styles.paragraph}>How are establishments and employment distributed across firm sizes in {displayMunicname}?</Text>
                <Text style={styles.paragraph}>
                  This chart compares the share of establishments and FTE employment across firm-size categories in the selected year, showing the selected place alongside provincial and national benchmarks. It shows whether jobs are concentrated in a small number of large firms or distributed across many smaller businesses, revealing the structure of the local economy.
                </Text>
              </View>
              <View style={styles.footerContainer}>
                <Text style={styles.footer}>
                  www.spatialtaxdata.org.za
                </Text>
              </View>
            </Page>
          </Document>
        </PDFViewer>
      ) : null}
      {/* Off-screen rendering: 3 charts side-by-side for image capture */}
      <div style={{ position: "absolute", left: "-10000px" }}>
        <div ref={chartRef} style={{ display: "flex", width: 700, backgroundColor: "#fff" }}>
          {charts.map((chart, i) => (
            <div key={i} style={{ width: chartWidthPx }}>
              <div style={{ textAlign: "center", fontSize: 11, fontWeight: 600, marginBottom: 4, paddingLeft: 30, color: "#555" }}>{chart.subtitle}</div>
              <ResponsiveContainer width={chartWidthPx} height={400}>
                <BarChart
                  data={chart.data.chartData}
                  margin={{ top: 10, right: 10, left: 5, bottom: 20 }}
                >
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 20, 40, 60, 80, 100]}
                    tick={i === 0 ? { fontSize: 10 } : false}
                    label={i === 0 ? { value: "% of Total", angle: -90, dx: -25, fontSize: 10 } : false}
                    axisLine={i === 0}
                    tickLine={i === 0}
                    width={i === 0 ? 50 : 50}
                  />
                  {segmentNames.map((seg, si) => (
                    <Bar key={seg} dataKey={seg} stackId="a" fill={SEGMENT_COLORS[si]}>
                      <LabelList dataKey={seg} content={SegmentLabel} />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default EstablishmentsVsEmploymentPdf;
